import { useEffect, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Box, MenuItem, Typography,
} from "@mui/material";
import { useScriptMode } from "../../../contexts/ScriptModeContext";
import { SoftButton, useDialogPaperSx } from "../../hatim/soft/SoftKit";
import { useCreateHatimTemplateMutation } from "../../../api/hatimManagementApi";

const TYPE_KEYS = [
  ["Kuran-ı Kerim", "hatim_mgmt_create_type_kuran"],
  ["Cevşen", "hatim_mgmt_create_type_cevsen"],
  ["Salavat", "hatim_mgmt_create_type_salavat"],
  ["Dua", "hatim_mgmt_create_type_dua"],
  ["Tesbih", "hatim_mgmt_create_type_tesbih"],
  ["Diğer", "hatim_mgmt_create_type_other"],
];

// Mobil createHatim ile aynı: Kuran 600 sayfa sabit, Cevşen 40 varsayılan.
const KURAN_TYPE = "Kuran-ı Kerim";
const KURAN_TOTAL = 600;
const TYPE_DEFAULT_TOTAL = { [KURAN_TYPE]: KURAN_TOTAL, "Cevşen": 40 };

const DURATION_KEYS = [
  ["daily", "hatim_mgmt_create_duration_daily"],
  ["weekly", "hatim_mgmt_create_duration_weekly"],
  ["monthly", "hatim_mgmt_create_duration_monthly"],
  ["custom", "hatim_mgmt_create_duration_custom"],
];

/**
 * Mobil createHatim ile aynı: yenilenme saati sunucuya ayrı alan olarak
 * gitmez; başlangıç ve bitiş tarihine yerel saat olarak eklenip ISO gönderilir.
 */
export function combineDateAndTimeToIso(ymd, hhmm) {
  const [y, m, d] = String(ymd || "").split("-").map((n) => parseInt(n, 10));
  const [hh, mm] = String(hhmm || "00:00").split(":").map((n) => parseInt(n, 10));
  const date = new Date(y, (m || 1) - 1, d || 1, Number.isNaN(hh) ? 0 : hh, Number.isNaN(mm) ? 0 : mm, 0, 0);
  return date.toISOString();
}

export default function CreateHatimTemplateModal({ open, onClose, onCreated }) {
  const { t } = useScriptMode();
  const paperSx = useDialogPaperSx();
  const [name, setName] = useState("");
  const [type, setType] = useState("Kuran-ı Kerim");
  const [description, setDescription] = useState("");
  const [totalAmount, setTotalAmount] = useState("600");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [resetTime, setResetTime] = useState("00:00");
  const [duration, setDuration] = useState("weekly");
  const [durationDays, setDurationDays] = useState("");
  const [repeatDuration, setRepeatDuration] = useState("");
  const [error, setError] = useState("");
  const [createTpl, { isLoading }] = useCreateHatimTemplateMutation();

  useEffect(() => {
    if (!open) {
      setName("");
      setType("Kuran-ı Kerim");
      setDescription("");
      setTotalAmount("600");
      setStartDate("");
      setEndDate("");
      setResetTime("00:00");
      setDuration("weekly");
      setDurationDays("");
      setRepeatDuration("");
      setError("");
    }
  }, [open]);

  const isKuran = type === KURAN_TYPE;
  const handleTypeChange = (next) => {
    setType(next);
    if (TYPE_DEFAULT_TOTAL[next] !== undefined) setTotalAmount(String(TYPE_DEFAULT_TOTAL[next]));
  };

  const trimmedName = name.trim();
  const totalNum = isKuran ? KURAN_TOTAL : Number(totalAmount);
  const durationDaysNum = Number(durationDays);
  const repeatNum = Number(repeatDuration);
  const canSubmit =
    trimmedName.length >= 2 &&
    !!type &&
    Number.isFinite(totalNum) &&
    totalNum >= 1 &&
    !!startDate &&
    !!endDate &&
    startDate <= endDate &&
    (duration !== "custom" || (Number.isFinite(durationDaysNum) && durationDaysNum >= 1)) &&
    (repeatDuration === "" || (Number.isFinite(repeatNum) && repeatNum >= 1)) &&
    !isLoading;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    try {
      const body = {
        name: trimmedName,
        type,
        description: description.trim(),
        totalAmount: totalNum,
        startDate: combineDateAndTimeToIso(startDate, resetTime),
        endDate: combineDateAndTimeToIso(endDate, resetTime),
        duration,
      };
      if (duration === "custom") body.durationDays = durationDaysNum;
      if (repeatDuration !== "") body.repeatDuration = repeatNum;
      const res = await createTpl(body).unwrap();
      onCreated && onCreated(res?.data?.template?.id);
      onClose && onClose();
    } catch (err) {
      const msg = err?.message || err?.data?.message || t("hatim_mgmt_create_error");
      setError(msg);
    }
  };

  return (
    <Dialog open={!!open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: paperSx }}>
      <DialogTitle sx={{ fontWeight: 500 }}>{t("hatim_mgmt_create_modal_title")}</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} mt={1}>
          <TextField
            autoFocus
            label={t("hatim_mgmt_create_name")}
            value={name}
            onChange={(e) => setName(e.target.value)}
            inputProps={{ maxLength: 60 }}
          />
          <TextField
            select
            label={t("hatim_mgmt_create_type")}
            value={type}
            onChange={(e) => handleTypeChange(e.target.value)}
          >
            {TYPE_KEYS.map(([val, key]) => (
              <MenuItem key={val} value={val}>
                {t(key)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            type="number"
            label={t("hatim_mgmt_create_total_amount")}
            value={isKuran ? String(KURAN_TOTAL) : totalAmount}
            onChange={(e) => setTotalAmount(e.target.value)}
            inputProps={{ min: 1, step: 1, readOnly: isKuran }}
            disabled={isKuran}
            helperText={isKuran ? t("hatim_mgmt_create_total_fixed_kuran") : undefined}
          />
          <TextField
            label={t("hatim_mgmt_create_description")}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            minRows={2}
          />
          <Box display="flex" gap={2}>
            <TextField
              type="date"
              fullWidth
              label={t("hatim_mgmt_create_start_date")}
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              type="date"
              fullWidth
              label={t("hatim_mgmt_create_end_date")}
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
          <TextField
            type="time"
            label={t("Yenilenme saati")}
            value={resetTime}
            onChange={(e) => setResetTime(e.target.value || "00:00")}
            InputLabelProps={{ shrink: true }}
            inputProps={{ step: 300 }}
            helperText={t("Her gün bu saatte hatim programı yenilenir.")}
          />
          <Box display="flex" gap={2}>
            <TextField
              select
              fullWidth
              label={t("hatim_mgmt_create_duration")}
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
            >
              {DURATION_KEYS.map(([val, key]) => (
                <MenuItem key={val} value={val}>
                  {t(key)}
                </MenuItem>
              ))}
            </TextField>
            {duration === "custom" ? (
              <TextField
                type="number"
                fullWidth
                label={t("hatim_mgmt_create_duration_days")}
                value={durationDays}
                onChange={(e) => setDurationDays(e.target.value)}
                inputProps={{ min: 1, step: 1 }}
              />
            ) : null}
          </Box>
          <TextField
            type="number"
            label={t("hatim_mgmt_create_repeat_duration")}
            value={repeatDuration}
            onChange={(e) => setRepeatDuration(e.target.value)}
            inputProps={{ min: 1, step: 1 }}
          />
          {error ? (
            <Typography color="error" variant="body2">{error}</Typography>
          ) : null}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <SoftButton tone="cancel" onClick={onClose}>{t("hatim_mgmt_create_cancel")}</SoftButton>
        <SoftButton tone="primary" onClick={handleSubmit} disabled={!canSubmit}>
          {t("hatim_mgmt_create_submit")}
        </SoftButton>
      </DialogActions>
    </Dialog>
  );
}
